import { createFileRoute } from "@tanstack/react-router";
import { startOfToday } from "date-fns";
import { useTranslation } from "react-i18next";
import z from "zod";
import { Container } from "@/components/admin/Container";
import { Calendar } from "@/components/admin/calendar/components/calendar";
import { getDateRange } from "@/components/admin/calendar/helpers";
import { eventsQueryOptions } from "@/components/admin/calendar/queries";
import type { TCalendarView } from "@/components/admin/calendar/types";
import { Header } from "@/components/admin/Header";
import { Searchbar } from "@/components/admin/Searchbar";
import { getActiveBranchId } from "@/hooks/use-active-branch";
import { capitalize } from "@/lib/utils";

const VIEWS: Array<TCalendarView> = ["day", "week", "month", "year", "agenda"];

const validateSearch = z.object({
	selectedDate: z.iso
		.datetime()
		.default(startOfToday().toString())
		.transform((value) => new Date(value)),
	view: z.enum(VIEWS).default("day"),
});

export const Route = createFileRoute("/dashboard/appointments")({
	validateSearch,
	beforeLoad: async ({ context, search }) => {
		const queryClient = context.queryClient;

		const { selectedDate, view } = search;

		const { startDate, endDate } = getDateRange(view, selectedDate);

		const activeBranchId = getActiveBranchId();

		await Promise.allSettled([queryClient.ensureQueryData(eventsQueryOptions({ startDate, endDate, activeBranchId }))]);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();

	return (
		<>
			<Header
				breadcrumbs={[
					{
						label: capitalize(t(($) => $.modules.dashboard.title)),
						href: "/dashboard",
					},
					{
						label: capitalize(t(($) => $.modules.appointments.title)),
					},
				]}
			>
				<div className="ml-auto flex items-center space-x-4">
					<Searchbar />
				</div>
			</Header>
			<Container className="flex flex-col gap-4 p-4 lg:flex-row">
				<Calendar />
			</Container>
		</>
	);
}
